import {
  useMemo,
  useState,
} from 'react';

import {
  Box,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type {
  MarginCenterComparisonMode,
  MarginCenterTimeGrain,
} from '../types/marginCenter';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


type TrendMetricKey =
  | 'revenue'
  | 'margin_amount'
  | 'margin_percentage'
  | 'units'
  | 'transaction_count'
  | 'average_net_unit_price'
  | 'average_unit_cost';

type TrendMetricFormat =
  | 'currency'
  | 'percentage'
  | 'number'
  | 'integer';


interface EntityTrendSeriesPoint {
  period: 'current' | 'comparison';

  bucket_index: number;

  covered_start: string;
  covered_end: string;

  is_partial_bucket: boolean;

  transaction_count: number;

  units: number;
  revenue: number;

  cost_amount: number | null;
  margin_amount: number | null;
  margin_percentage: number | null;

  average_net_unit_price: number | null;
  average_unit_cost: number | null;
}


interface EntityTrendSelection {
  start_date: string | null;
  end_date: string | null;

  comparison_mode: MarginCenterComparisonMode;
  comparison_start_date: string | null;
  comparison_end_date: string | null;

  time_grain: MarginCenterTimeGrain;
}


interface TrendChartPoint {
  bucketIndex: number;
  label: string;

  currentStart: string;
  currentEnd: string;

  comparisonStart: string | null;
  comparisonEnd: string | null;

  currentValue: number | null;
  comparisonValue: number | null;

  isPartialBucket: boolean;
}


const METRICS: Array<{
  key: TrendMetricKey;
  label: string;
  format: TrendMetricFormat;
  maximumFractionDigits: number;
}> = [
  {
    key: 'revenue',
    label: 'Ingresos',
    format: 'currency',
    maximumFractionDigits: 0,
  },
  {
    key: 'margin_amount',
    label: 'Margen',
    format: 'currency',
    maximumFractionDigits: 0,
  },
  {
    key: 'margin_percentage',
    label: 'Margen %',
    format: 'percentage',
    maximumFractionDigits: 1,
  },
  {
    key: 'units',
    label: 'Unidades',
    format: 'number',
    maximumFractionDigits: 2,
  },
  {
    key: 'transaction_count',
    label: 'Transacciones',
    format: 'integer',
    maximumFractionDigits: 0,
  },
  {
    key: 'average_net_unit_price',
    label: 'Precio neto',
    format: 'currency',
    maximumFractionDigits: 2,
  },
  {
    key: 'average_unit_cost',
    label: 'Coste unitario',
    format: 'currency',
    maximumFractionDigits: 2,
  },
];


function formatShortDate(
  value: string | null | undefined,
): string {
  if (!value) {
    return '—';
  }

  const normalized =
    value.slice(0, 10);

  const [
    year,
    month,
    day,
  ] = normalized
    .split('-')
    .map(Number);

  if (
    !year
    || !month
    || !day
  ) {
    return normalized;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: 'short',
    },
  ).format(
    new Date(
      year,
      month - 1,
      day,
    ),
  );
}


function formatFullDate(
  value: string | null | undefined,
): string {
  if (!value) {
    return '—';
  }

  const normalized =
    value.slice(0, 10);

  const [
    year,
    month,
    day,
  ] = normalized
    .split('-')
    .map(Number);

  if (
    !year
    || !month
    || !day
  ) {
    return normalized;
  }

  return new Intl.DateTimeFormat(
    'es-ES',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  ).format(
    new Date(
      year,
      month - 1,
      day,
    ),
  );
}


function formatMetricValue(
  value: number | null | undefined,
  format: TrendMetricFormat,
  maximumFractionDigits: number,
  currencyCode: string | null,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (format === 'percentage') {
    return `${
      pricingNumberFormat({
          maximumFractionDigits,
        },
      ).format(value)
    } %`;
  }

  if (
    format === 'currency'
    && currencyCode
  ) {
    try {
      return pricingNumberFormat({
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits,
        },
      ).format(value);
    } catch {
      // Fallback below.
    }
  }

  return pricingNumberFormat({
      maximumFractionDigits:
        format === 'integer'
          ? 0
          : maximumFractionDigits,
    },
  ).format(value);
}


export default function MarginCenterEntityTrendPanel({
  series,
  selection,
  currencyCode,
}: {
  series: EntityTrendSeriesPoint[];
  selection: EntityTrendSelection;
  currencyCode: string | null;
}) {
  const [
    selectedMetricKey,
    setSelectedMetricKey,
  ] = useState<TrendMetricKey>(
    'revenue',
  );

  const availableMetrics =
    useMemo(
      () =>
        METRICS.filter(
          (metric) =>
            series.some(
              (point) =>
                point.period === 'current'
                && point[metric.key]
                  !== null,
            ),
        ),
      [series],
    );

  const selectedMetric =
    availableMetrics.find(
      (metric) =>
        metric.key
        === selectedMetricKey,
    )
    ?? availableMetrics[0]
    ?? METRICS[0];

  const chartData =
    useMemo<TrendChartPoint[]>(
      () => {
        const currentSeries =
          series
            .filter(
              (point) =>
                point.period
                === 'current',
            )
            .sort(
              (left, right) =>
                left.bucket_index
                - right.bucket_index,
            );

        const comparisonByBucket =
          new Map(
            series
              .filter(
                (point) =>
                  point.period
                  === 'comparison',
              )
              .map(
                (point) => [
                  point.bucket_index,
                  point,
                ] as const,
              ),
          );

        return currentSeries.map(
          (point) => {
            const comparisonPoint =
              comparisonByBucket.get(
                point.bucket_index,
              );

            return {
              bucketIndex:
                point.bucket_index,

              label:
                formatShortDate(
                  point.covered_start,
                ),

              currentStart:
                point.covered_start,

              currentEnd:
                point.covered_end,

              comparisonStart:
                comparisonPoint
                  ?.covered_start
                ?? null,

              comparisonEnd:
                comparisonPoint
                  ?.covered_end
                ?? null,

              currentValue:
                point[
                  selectedMetric.key
                ],

              comparisonValue:
                comparisonPoint
                  ? comparisonPoint[
                    selectedMetric.key
                  ]
                  : null,

              isPartialBucket:
                point.is_partial_bucket,
            };
          },
        );
      },
      [
        selectedMetric.key,
        series,
      ],
    );

  const hasComparison =
    selection.comparison_mode
    !== 'none';

  const hasPartialBucket =
    chartData.some(
      (point) =>
        point.isPartialBucket,
    );

  const formatValue = (
    value: number | null | undefined,
  ) =>
    formatMetricValue(
      value,
      selectedMetric.format,
      selectedMetric
        .maximumFractionDigits,
      currencyCode,
    );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border:
          '1px solid rgba(148,163,184,0.14)',
        background:
          'linear-gradient(180deg, rgba(18,27,47,0.68) 0%, rgba(10,18,33,0.84) 100%)',
        boxShadow:
          '0 14px 34px rgba(2,6,23,0.16)',
        minWidth: 0,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          spacing={1.5}
          alignItems={{
            xs: 'stretch',
            md: 'flex-start',
          }}
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: '#f8fafc',
                fontWeight: 800,
              }}
            >
              Evolución temporal
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                mt: 0.25,
              }}
            >
              Periodo actual y comparación alineados por
              posición temporal equivalente.
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              flexWrap="wrap"
              sx={{
                mt: 0.8,
              }}
            >
              <Stack
                direction="row"
                spacing={0.6}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 18,
                    height: 2,
                    bgcolor: '#38bdf8',
                    borderRadius: 99,
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    color: '#94a3b8',
                  }}
                >
                  Actual ·{' '}
                  {formatFullDate(
                    selection.start_date,
                  )}
                  {' — '}
                  {formatFullDate(
                    selection.end_date,
                  )}
                </Typography>
              </Stack>

              {hasComparison
                && selection
                  .comparison_start_date
                && selection
                  .comparison_end_date
                && (
                <Stack
                  direction="row"
                  spacing={0.6}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 18,
                      borderTop:
                        '2px dashed #94a3b8',
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                    }}
                  >
                    Comparación ·{' '}
                    {formatFullDate(
                      selection
                        .comparison_start_date,
                    )}
                    {' — '}
                    {formatFullDate(
                      selection
                        .comparison_end_date,
                    )}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={
              selectedMetric.key
            }
            onChange={(
              _,
              nextMetric:
                TrendMetricKey | null,
            ) => {
              if (!nextMetric) {
                return;
              }

              setSelectedMetricKey(
                nextMetric,
              );
            }}
            sx={{
              flexWrap: 'wrap',
              justifyContent:
                'flex-end',

              '& .MuiToggleButton-root':
                {
                  px: 1.1,
                  py: 0.45,
                  textTransform:
                    'none',
                  color: '#94a3b8',
                  borderColor:
                    'rgba(148,163,184,0.18)',

                  '&.Mui-selected': {
                    color: '#f8fafc',
                    bgcolor:
                      'rgba(56,189,248,0.12)',
                  },

                  '&.Mui-selected:hover':
                    {
                      bgcolor:
                        'rgba(56,189,248,0.16)',
                    },
                },
            }}
          >
            {availableMetrics.map(
              (metric) => (
                <ToggleButton
                  key={metric.key}
                  value={metric.key}
                >
                  {metric.label}
                </ToggleButton>
              ),
            )}
          </ToggleButtonGroup>
        </Stack>

        <Box
          sx={{
            width: '100%',
            height: 300,
          }}
        >
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{
                top: 8,
                right: 12,
                bottom: 4,
                left: 4,
              }}
            >
              <CartesianGrid
                vertical={false}
                stroke="rgba(148,163,184,0.12)"
                strokeDasharray="3 5"
              />

              <XAxis
                dataKey="label"
                axisLine={{
                  stroke:
                    'rgba(148,163,184,0.20)',
                }}
                tickLine={false}
                minTickGap={46}
                tick={{
                  fill: '#64748b',
                  fontSize: 11,
                }}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                width={82}
                tick={{
                  fill: '#64748b',
                  fontSize: 11,
                }}
                tickFormatter={(
                  value,
                ) =>
                  formatValue(
                    Number(value),
                  )
                }
              />

              <RechartsTooltip
                cursor={{
                  stroke:
                    'rgba(56,189,248,0.28)',
                  strokeWidth: 1,
                }}
                content={({
                  active,
                  payload,
                }) => {
                  if (
                    !active
                    || !payload
                    || payload.length
                      === 0
                  ) {
                    return null;
                  }

                  const point:
                    TrendChartPoint
                    | undefined =
                      payload[0]
                        ?.payload;

                  if (!point) {
                    return null;
                  }

                  return (
                    <Paper
                      elevation={8}
                      sx={{
                        minWidth: 250,
                        p: 1.4,
                        bgcolor:
                          '#0b1220',
                        border:
                          '1px solid rgba(148,163,184,0.20)',
                        borderRadius: 2,
                      }}
                    >
                      <Stack spacing={1}>
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                '#38bdf8',
                              fontWeight:
                                800,
                            }}
                          >
                            Periodo actual
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              display:
                                'block',
                              color:
                                '#64748b',
                            }}
                          >
                            {formatFullDate(
                              point
                                .currentStart,
                            )}
                            {' — '}
                            {formatFullDate(
                              point
                                .currentEnd,
                            )}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                '#f8fafc',
                              fontWeight:
                                850,
                              mt: 0.1,
                            }}
                          >
                            {formatValue(
                              point
                                .currentValue,
                            )}
                          </Typography>
                        </Box>

                        {hasComparison
                          && point
                            .comparisonStart
                          && point
                            .comparisonEnd
                          && (
                          <Box
                            sx={{
                              pt: 0.8,
                              borderTop:
                                '1px solid rgba(148,163,184,0.10)',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  '#94a3b8',
                                fontWeight:
                                  800,
                              }}
                            >
                              Periodo comparado
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                display:
                                  'block',
                                color:
                                  '#64748b',
                              }}
                            >
                              {formatFullDate(
                                point
                                  .comparisonStart,
                              )}
                              {' — '}
                              {formatFullDate(
                                point
                                  .comparisonEnd,
                              )}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  '#e2e8f0',
                                fontWeight:
                                  800,
                                mt: 0.1,
                              }}
                            >
                              {formatValue(
                                point
                                  .comparisonValue,
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  );
                }}
              />

              <Line
                type="monotone"
                dataKey="currentValue"
                name="Periodo actual"
                stroke="#38bdf8"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />

              {hasComparison && (
                <Line
                  type="monotone"
                  dataKey="comparisonValue"
                  name="Comparación"
                  stroke="#94a3b8"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={false}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {hasPartialBucket && (
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
            }}
          >
            La selección contiene al menos un tramo
            temporal parcial.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}